import Layout from "@/layouts/layout";
import PageHeading from "@/components/layout/PageHeading";
import Panel from "@/components/ui/Panel";
import Button, { ButtonLink } from "@/components/ui/Button";
import { Field, Input, Textarea, Checkbox } from "@/components/ui/Field";
import { useForm, router } from "@inertiajs/react";
import { toDatetimeLocal } from "@/utils/formatEventDate";
import type { CheckInCall } from "@/interfaces/check_in_call";

export default function AdminCheckInCallEdit({
  call,
}: {
  call: Partial<CheckInCall>;
}) {
  const isNew = !call.id;

  const { data, setData, transform, post, patch, processing, errors } = useForm(
    {
      title: call.title ?? "Organizer check-in call",
      agenda: call.agenda ?? "",
      // datetime-local is wall clock with no zone, so this reads and writes in
      // *your* browser's zone and the ISO conversion below pins it to an
      // instant. Organizers each see it converted back into their own.
      starts_at: toDatetimeLocal(call.starts_at),
      duration_minutes: call.duration_minutes ?? 30,
      zoom_url: call.zoom_url ?? "",
      published: call.published ?? true,
    },
  );

  transform((d) => ({
    ...d,
    starts_at: d.starts_at ? new Date(d.starts_at).toISOString() : "",
  }));

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (isNew) {
      post("/admin/check-in-calls");
    } else {
      patch(`/admin/check-in-calls/${call.id}`);
    }
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8">
        <PageHeading
          eyebrow="Admin"
          title={isNew ? "New check-in call" : call.title || "Check-in call"}
          actions={
            <ButtonLink href="/admin/check-in-calls" variant="secondary">
              All calls
            </ButtonLink>
          }
        />

        <form onSubmit={submit}>
          <Panel title="call.form" className="max-w-2xl">
            <div className="flex flex-col gap-5">
              <Field label="Title" error={errors.title}>
                <Input
                  value={data.title}
                  onChange={(e) => setData("title", e.target.value)}
                  required
                />
              </Field>

              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <Field
                  label="Starts"
                  hint="In your local time. Organizers see it in theirs."
                  error={errors.starts_at}
                >
                  <Input
                    type="datetime-local"
                    value={data.starts_at}
                    onChange={(e) => setData("starts_at", e.target.value)}
                    required
                  />
                </Field>

                <Field label="Length (minutes)" error={errors.duration_minutes}>
                  <Input
                    type="number"
                    min={5}
                    max={480}
                    value={data.duration_minutes}
                    onChange={(e) =>
                      setData("duration_minutes", Number(e.target.value))
                    }
                    className="max-w-40"
                  />
                </Field>
              </div>

              <Field
                label="Zoom link"
                hint="Only organizers see this. Safe to leave blank now and add it before the call."
                error={errors.zoom_url}
              >
                <Input
                  value={data.zoom_url}
                  onChange={(e) => setData("zoom_url", e.target.value)}
                  placeholder="https://zoom.us/j/…"
                />
              </Field>

              <Field
                label="Agenda"
                hint="Shown on every organizer's dashboard. What you'll cover."
                error={errors.agenda}
              >
                <Textarea
                  rows={4}
                  value={data.agenda}
                  onChange={(e) => setData("agenda", e.target.value)}
                  placeholder="Venue check, boba orders, and anything you're stuck on."
                />
              </Field>

              <label className="flex items-center gap-3">
                <Checkbox
                  checked={data.published}
                  onChange={(e) => setData("published", e.target.checked)}
                />
                <span className="font-display text-subheading font-bold">
                  Visible to organizers
                </span>
              </label>

              <div className="flex flex-wrap gap-3">
                <Button type="submit" disabled={processing}>
                  {processing ? "Saving…" : isNew ? "Schedule call" : "Save"}
                </Button>

                {!isNew && (
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => {
                      if (confirm(`Cancel "${call.title}"?`)) {
                        router.delete(`/admin/check-in-calls/${call.id}`);
                      }
                    }}
                  >
                    Cancel call
                  </Button>
                )}
              </div>
            </div>
          </Panel>
        </form>
      </div>
    </Layout>
  );
}
